import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Check, Copy, Link, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchShareStatus, toggleShare } from "@/lib/api";

const FRONTEND_URL = window.location.origin;

export const ShareDialog = ({ open, onOpenChange }) => {
  const [shareToken, setShareToken] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = shareToken ? `${FRONTEND_URL}/share/${shareToken}` : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchShareStatus();
      setShareToken(data.share_token);
      setEnabled(data.enabled);
    } catch {
      toast.error("Could not load share status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const data = await toggleShare();
      setShareToken(data.share_token);
      setEnabled(data.enabled);
      if (data.enabled) toast.success("Sharing enabled");
      else toast.success("Sharing disabled");
    } catch {
      toast.error("Could not update sharing");
    } finally {
      setToggling(false);
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] overflow-hidden bg-[#F9F8F6] border-stone-200">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium tracking-tight">
            Share your shortlist
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Anyone with the link can view your rentals and add new ones.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : (
          <div className="space-y-5 mt-1">
            <div className="flex items-center justify-between bg-white border border-stone-200 rounded-2xl px-5 py-4">
              <div>
                <Label className="text-sm font-medium text-stone-800">
                  Enable sharing
                </Label>
                <p className="text-xs text-stone-500 mt-0.5">
                  {enabled ? "Your list is publicly accessible via the link below" : "Off — your list is private"}
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={toggling}
                className="data-[state=checked]:bg-[#4A5D4E]"
              />
            </div>

            {enabled && shareUrl && (
              <div className="space-y-2">
                <Label className="overline">Share link</Label>
                <div className="flex items-center gap-2 min-w-0 bg-white border border-stone-200 rounded-xl px-4 py-3">
                  <Link size={14} className="text-stone-400 shrink-0" />
                  <span className="text-sm text-stone-700 truncate flex-1 min-w-0">
                    {shareUrl}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copy}
                    className="shrink-0 text-stone-500 hover:text-stone-900 h-7 px-2"
                  >
                    {copied ? (
                      <Check size={14} className="text-[#4A5D4E]" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-stone-400 leading-relaxed">
                  Disabling sharing invalidates this link. Re-enabling generates
                  a new one.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
